import React, { useState, useEffect, useCallback } from 'react';
import { Card, Table, Form, Button, Spinner, Alert, Badge, Modal } from 'react-bootstrap';
import authApi from '../../config/authApi';

const AVAILABLE_MODULES = ['roundrobin', 'survey', 'inbound'];

export default function AdminPanel() {
  const [domains, setDomains] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [saving, setSaving] = useState({});

  // Add domain modal
  const [showAdd, setShowAdd] = useState(false);
  const [newSubdomain, setNewSubdomain] = useState('');
  const [newModules, setNewModules] = useState({});

  const fetchDomains = useCallback(async () => {
    setLoading(true);
    try {
      const res = await authApi.get('/admin/domains');
      setDomains(res.data);
    } catch (err) {
      setMessage({ type: 'danger', text: 'Failed to load domains' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchDomains(); }, [fetchDomains]);

  const toggleModule = async (subdomain, moduleKey, currentEnabled) => {
    setSaving((prev) => ({ ...prev, [`${subdomain}-${moduleKey}`]: true }));
    try {
      await authApi.put(`/admin/domains/${subdomain}/modules`, {
        modules: { [moduleKey]: !currentEnabled },
      });
      setMessage({ type: 'success', text: `Updated ${moduleKey} for ${subdomain}` });
      fetchDomains();
    } catch (err) {
      setMessage({ type: 'danger', text: 'Update failed' });
    } finally {
      setSaving((prev) => ({ ...prev, [`${subdomain}-${moduleKey}`]: false }));
    }
  };

  const handleAddDomain = async () => {
    if (!newSubdomain.trim()) return;
    try {
      await authApi.put(`/admin/domains/${newSubdomain.trim()}/modules`, {
        modules: newModules,
      });
      setMessage({ type: 'success', text: `Domain ${newSubdomain} added` });
      setShowAdd(false);
      setNewSubdomain('');
      setNewModules({});
      fetchDomains();
    } catch (err) {
      setMessage({ type: 'danger', text: 'Failed to add domain' });
    }
  };

  const isModuleEnabled = (domain, key) => {
    return domain.modules?.[key]?.enabled || false;
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h4 style={{ fontWeight: 700, color: '#0f172a', margin: 0 }}>
          Admin Panel
        </h4>
        <Button variant="primary" onClick={() => setShowAdd(true)}>
          Add Domain
        </Button>
      </div>

      {message && (
        <Alert variant={message.type} dismissible onClose={() => setMessage(null)}>
          {message.text}
        </Alert>
      )}

      <Card>
        <Card.Header>
          <strong>Domain Module Access</strong>
        </Card.Header>
        <Card.Body className="p-0">
          {loading ? (
            <div className="text-center py-5"><Spinner /></div>
          ) : domains.length === 0 ? (
            <div className="text-center py-5 text-muted">
              No domains registered. Domains are created automatically when users sign in via Zendesk.
            </div>
          ) : (
            <Table hover responsive className="mb-0">
              <thead className="table-light">
                <tr>
                  <th>Subdomain</th>
                  {AVAILABLE_MODULES.map((m) => (
                    <th key={m} className="text-center">{m}</th>
                  ))}
                  <th>Created</th>
                </tr>
              </thead>
              <tbody>
                {domains.map((domain) => (
                  <tr key={domain.subdomain}>
                    <td>
                      <strong>{domain.subdomain}</strong>
                      <span style={{ color: '#94a3b8', fontSize: '0.8rem' }}>.zendesk.com</span>
                    </td>
                    {AVAILABLE_MODULES.map((key) => {
                      const enabled = isModuleEnabled(domain, key);
                      const isSaving = saving[`${domain.subdomain}-${key}`];
                      return (
                        <td key={key} className="text-center">
                          {isSaving ? (
                            <Spinner size="sm" />
                          ) : (
                            <Form.Check
                              type="switch"
                              checked={enabled}
                              onChange={() =>
                                toggleModule(domain.subdomain, key, enabled)
                              }
                            />
                          )}
                        </td>
                      );
                    })}
                    <td>
                      <small className="text-muted">
                        {domain.created_at
                          ? new Date(domain.created_at).toLocaleDateString()
                          : '-'}
                      </small>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      {/* Add Domain Modal */}
      <Modal show={showAdd} onHide={() => setShowAdd(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Add Domain</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Zendesk Subdomain</Form.Label>
            <Form.Control
              value={newSubdomain}
              onChange={(e) => setNewSubdomain(e.target.value)}
              placeholder="yourcompany"
            />
          </Form.Group>
          <Form.Group>
            <Form.Label>Enable Modules</Form.Label>
            {AVAILABLE_MODULES.map((key) => (
              <Form.Check
                key={key}
                type="switch"
                label={key}
                checked={newModules[key] || false}
                onChange={(e) =>
                  setNewModules((prev) => ({ ...prev, [key]: e.target.checked }))
                }
                className="mb-2"
              />
            ))}
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowAdd(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleAddDomain}
            disabled={!newSubdomain.trim()}
          >
            Add
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
